import axios, { AxiosError, AxiosInstance, AxiosResponse } from "axios";
import axiosRetry from "axios-retry";
import { encode } from "base-64";

import { buildHeaders } from "./headers";
import {
  clearCachedInitToken,
  getToken,
  refreshInitToken,
} from "./token";
import type {
  AppAxiosRequestConfig,
  AppInternalAxiosRequestConfig,
  CPFData,
  ExtraRequestOptions,
  RequestBodyOptions,
  RequestMeta,
  ServiceClientConfig,
} from "./types";
import { normalizeError } from "./errors";

function toRequestMeta(cpf?: CPFData | null, isFile = false): RequestMeta {
  return {
    cpf: cpf ?? null,
    isFile,
  };
}

function toAxiosConfig(
  cpf?: CPFData | null,
  isFile = false,
  extraOptions?: ExtraRequestOptions
): AppAxiosRequestConfig {
  return {
    meta: toRequestMeta(cpf, isFile),
    signal: extraOptions?.signal,
    responseType: extraOptions?.responseType,
    params: extraOptions?.params,
    headers: extraOptions?.headers,
  };
}

function extractErrorResponse<T>(error: unknown): AxiosResponse<T> | null {
  if (axios.isAxiosError<T>(error)) {
    return error.response ?? null;
  }

  return null;
}

export function createServiceClient(config: ServiceClientConfig) {
  const instance: AxiosInstance = axios.create({
    baseURL: config.baseURL,
  });

  instance.interceptors.request.use(
    async (requestConfig: AppInternalAxiosRequestConfig) => {
      const bearerToken = await getToken(
        config.initTokenStorageKey,
        config.tipo
      );

      const headers = buildHeaders(
        config.tipo,
        config.awsAuthStorageKey,
        requestConfig.meta
      );

      requestConfig.headers.set(headers);

      if (bearerToken) {
        requestConfig.headers.set("Authorization", `Bearer ${bearerToken}`);
      }

      return requestConfig;
    },
    (error: unknown) => Promise.reject(normalizeError(error))
  );

  instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalConfig = error.config as AppAxiosRequestConfig | undefined;

      if (
        !originalConfig ||
        originalConfig._retry ||
        error.response?.status !== 401
      ) {
        return Promise.reject(normalizeError(error));
      }

      originalConfig._retry = true;

      clearCachedInitToken(config.initTokenStorageKey);

      const refreshedToken = await refreshInitToken(
        config.initTokenStorageKey,
        config.tipo
      );

      if (!refreshedToken) {
        return Promise.reject(normalizeError(error));
      }

      return instance(originalConfig);
    }
  );

  axiosRetry(instance, {
    retries: 5,
    retryDelay: (retryCount) => retryCount * 6000,
    retryCondition: (error) => error.response?.status === 504,
  });

  async function postRequest<TResponse = unknown, TBody = unknown>(
    url: string,
    options: RequestBodyOptions<TBody>,
    cpf: CPFData | null = null,
    isFile = false,
    extraOptions?: ExtraRequestOptions
  ): Promise<AxiosResponse<TResponse> | null> {
    try {
      return await instance.post<TResponse>(
        url,
        options.body,
        toAxiosConfig(cpf, isFile, extraOptions)
      );
    } catch (error) {
      return extractErrorResponse<TResponse>(error);
    }
  }

  async function putRequest<TResponse = unknown, TBody = unknown>(
    url: string,
    options: RequestBodyOptions<TBody>,
    cpf: CPFData | null = null,
    isFile = false,
    extraOptions?: ExtraRequestOptions
  ): Promise<AxiosResponse<TResponse> | null> {
    try {
      return await instance.put<TResponse>(
        url,
        options.body,
        toAxiosConfig(cpf, isFile, extraOptions)
      );
    } catch (error) {
      return extractErrorResponse<TResponse>(error);
    }
  }

  async function getRequest<TResponse = unknown>(
    url: string,
    cpf: CPFData | null = null,
    isFile = false,
    extraOptions?: ExtraRequestOptions
  ): Promise<AxiosResponse<TResponse> | null> {
    try {
      return await instance.get<TResponse>(
        url,
        toAxiosConfig(cpf, isFile, extraOptions)
      );
    } catch (error) {
      return extractErrorResponse<TResponse>(error);
    }
  }

  async function deleteRequest<TResponse = unknown>(
    url: string,
    cpf: CPFData | null = null,
    isFile = false,
    extraOptions?: ExtraRequestOptions
  ): Promise<AxiosResponse<TResponse> | null> {
    try {
      return await instance.delete<TResponse>(
        url,
        toAxiosConfig(cpf, isFile, extraOptions)
      );
    } catch (error) {
      return extractErrorResponse<TResponse>(error);
    }
  }

  async function downloadRequest(
    url: string,
    cpf: CPFData | null = null,
    extraOptions?: Omit<ExtraRequestOptions, "responseType">
  ): Promise<AxiosResponse<Blob> | null> {
    try {
      return await instance.get<Blob>(
        url,
        toAxiosConfig(cpf, true, {
          ...extraOptions,
          responseType: "blob",
        })
      );
    } catch (error) {
      return extractErrorResponse<Blob>(error);
    }
  }

  function encodeBasicCredential(value: string) {
    return encode(value);
  }

  return {
    instance,
    postRequest,
    putRequest,
    getRequest,
    deleteRequest,
    downloadRequest,
    encodeBasicCredential,
  };
}
